function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) { symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); } keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

function _defineProperty(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

import { getMainCarrier } from '../carrier.js';
import { withScope, getCurrentScope, getIsolationScope, getClient } from '../currentScopes.js';
import { getAsyncContextStrategy } from '../asyncContext/index.js';
import { DEBUG_BUILD } from '../debug-build.js';
import { SEMANTIC_ATTRIBUTE_SENTRY_SOURCE, SEMANTIC_ATTRIBUTE_SENTRY_SAMPLE_RATE } from '../semanticAttributes.js';
import { logger } from '../utils-hoist/logger.js';
import { generateTraceId } from '../utils-hoist/propagationContext.js';
import { propagationContextFromHeaders } from '../utils-hoist/tracing.js';
import { handleCallbackErrors } from '../utils/handleCallbackErrors.js';
import { hasSpansEnabled } from '../utils/hasSpansEnabled.js';
import { parseSampleRate } from '../utils/parseSampleRate.js';
import { _setSpanForScope, _getSpanForScope } from '../utils/spanOnScope.js';
import { spanToJSON, addChildSpanToSpan, spanIsSampled, spanTimeInputToSeconds, getRootSpan } from '../utils/spanUtils.js';
import { getDynamicSamplingContextFromSpan, freezeDscOnSpan } from './dynamicSamplingContext.js';
import { logSpanStart } from './logSpans.js';
import { sampleSpan } from './sampling.js';
import { SentryNonRecordingSpan } from './sentryNonRecordingSpan.js';
import { SentrySpan } from './sentrySpan.js';
import { SPAN_STATUS_ERROR } from './spanstatus.js';
import { setCapturedScopesOnSpan } from './utils.js';
var SUPPRESS_TRACING_KEY = '__SENTRY_SUPPRESS_TRACING__';
/**
 * Wraps a function with a transaction/span and finishes the span after the function is done.
 * The created span is the active span and will be used as parent by other spans created inside the function
 * and can be accessed via `Sentry.getActiveSpan()`, as long as the function is executed while the scope is active.
 *
 * If you want to create a span that is not set as active, use {@link startInactiveSpan}.
 *
 * You'll always get a span passed to the callback,
 * it may just be a non-recording span if the span is not sampled or if tracing is disabled.
 */

function startSpan(options, callback) {
  var acs = getAcs();

  if (acs.startSpan) {
    return acs.startSpan(options, callback);
  }

  var spanArguments = parseSentrySpanArguments(options);
  var {
    forceTransaction,
    parentSpan: customParentSpan,
    scope: customScope
  } = options; // We still need to fork a potentially passed scope, as we set the active span on it
  // and we need to ensure that it is cleaned up properly once the span ends.

  var customForkedScope = customScope === null || customScope === void 0 ? void 0 : customScope.clone();
  return withScope(customForkedScope, () => {
    // If `options.parentSpan` is defined, we want to wrap the callback in `withActiveSpan`
    var wrapper = getActiveSpanWrapper(customParentSpan);
    return wrapper(() => {
      var scope = getCurrentScope();
      var parentSpan = getParentSpan(scope);
      var shouldSkipSpan = options.onlyIfParent && !parentSpan;
      var activeSpan = shouldSkipSpan ? new SentryNonRecordingSpan() : createChildOrRootSpan({
        parentSpan,
        spanArguments,
        forceTransaction,
        scope
      });

      _setSpanForScope(scope, activeSpan);

      return handleCallbackErrors(() => callback(activeSpan), () => {
        // Only update the span status if it hasn't been changed yet, and the span is not yet finished
        var {
          status
        } = spanToJSON(activeSpan);

        if (activeSpan.isRecording() && (!status || status === 'ok')) {
          activeSpan.setStatus({
            code: SPAN_STATUS_ERROR,
            message: 'internal_error'
          });
        }
      }, () => {
        activeSpan.end();
      });
    });
  });
}
/**
 * Similar to `Sentry.startSpan`. Wraps a function with a transaction/span, but does not finish the span
 * after the function is done automatically. Use `span.end()` to end the span.
 *
 * The created span is the active span and will be used as parent by other spans created inside the function
 * and can be accessed via `Sentry.getActiveSpan()`, as long as the function is executed while the scope is active.
 *
 * You'll always get a span passed to the callback,
 * it may just be a non-recording span if the span is not sampled or if tracing is disabled.
 */


function startSpanManual(options, callback) {
  var acs = getAcs();

  if (acs.startSpanManual) {
    return acs.startSpanManual(options, callback);
  }

  var spanArguments = parseSentrySpanArguments(options);
  var {
    forceTransaction,
    parentSpan: customParentSpan,
    scope: customScope
  } = options;
  var customForkedScope = customScope === null || customScope === void 0 ? void 0 : customScope.clone();
  return withScope(customForkedScope, () => {
    // If `options.parentSpan` is defined, we want to wrap the callback in `withActiveSpan`
    var wrapper = getActiveSpanWrapper(customParentSpan);
    return wrapper(() => {
      var scope = getCurrentScope();
      var parentSpan = getParentSpan(scope);
      var shouldSkipSpan = options.onlyIfParent && !parentSpan;
      var activeSpan = shouldSkipSpan ? new SentryNonRecordingSpan() : createChildOrRootSpan({
        parentSpan,
        spanArguments,
        forceTransaction,
        scope
      });

      _setSpanForScope(scope, activeSpan);

      return handleCallbackErrors( // We pass the `finish` function to the callback, so the user can finish the span manually
      // this is mainly here for historic purposes because previously, we instructed users to call
      // `finish` instead of `span.end()` to also clean up the scope. Nowadays, calling `span.end()`
      // or `finish` has the same effect and we simply leave it here to avoid breaking user code.
      () => callback(activeSpan, () => activeSpan.end()), () => {
        // Only update the span status if it hasn't been changed yet, and the span is not yet finished
        var {
          status
        } = spanToJSON(activeSpan);

        if (activeSpan.isRecording() && (!status || status === 'ok')) {
          activeSpan.setStatus({
            code: SPAN_STATUS_ERROR,
            message: 'internal_error'
          });
        }
      });
    });
  });
}
/**
 * Creates a span. This span is not set as active, so will not get automatic instrumentation spans
 * as children or be able to be accessed via `Sentry.getActiveSpan()`.
 *
 * If you want to create a span that is set as active, use {@link startSpan}.
 *
 * This function will always return a span,
 * it may just be a non-recording span if the span is not sampled or if tracing is disabled.
 */


function startInactiveSpan(options) {
  var acs = getAcs();

  if (acs.startInactiveSpan) {
    return acs.startInactiveSpan(options);
  }

  var spanArguments = parseSentrySpanArguments(options);
  var {
    forceTransaction,
    parentSpan: customParentSpan
  } = options; // If `options.scope` is defined, we use this as as a wrapper,
  // If `options.parentSpan` is defined, we want to wrap the callback in `withActiveSpan`

  var wrapper = options.scope ? callback => withScope(options.scope, callback) : customParentSpan !== undefined ? callback => withActiveSpan(customParentSpan, callback) : callback => callback();
  return wrapper(() => {
    var scope = getCurrentScope();
    var parentSpan = getParentSpan(scope);
    var shouldSkipSpan = options.onlyIfParent && !parentSpan;

    if (shouldSkipSpan) {
      return new SentryNonRecordingSpan();
    }

    return createChildOrRootSpan({
      parentSpan,
      spanArguments,
      forceTransaction,
      scope
    });
  });
}
/**
 * Continue a trace from `sentry-trace` and `baggage` values.
 * These values can be obtained from incoming request headers, or in the browser from `<meta name="sentry-trace">`
 * and `<meta name="baggage">` HTML tags.
 *
 * Spans started with `startSpan`, `startSpanManual` and `startInactiveSpan`, within the callback will automatically
 * be attached to the incoming trace.
 */


var continueTrace = (options, callback) => {
  var carrier = getMainCarrier();
  var acs = getAsyncContextStrategy(carrier);

  if (acs.continueTrace) {
    return acs.continueTrace(options, callback);
  }

  var {
    sentryTrace,
    baggage
  } = options;
  return withScope(scope => {
    var propagationContext = propagationContextFromHeaders(sentryTrace, baggage);
    scope.setPropagationContext(propagationContext);
    return callback();
  });
};
/**
 * Forks the current scope and sets the provided span as active span in the context of the provided callback. Can be
 * passed `null` to start an entirely new span tree.
 *
 * @param span Spans started in the context of the provided callback will be children of this span. If `null` is passed,
 * spans started within the callback will not be attached to a parent span.
 * @param callback Execution context in which the provided span will be active. Is passed the newly forked scope.
 * @returns the value returned from the provided callback function.
 */


function withActiveSpan(span, callback) {
  var acs = getAcs();

  if (acs.withActiveSpan) {
    return acs.withActiveSpan(span, callback);
  }

  return withScope(scope => {
    _setSpanForScope(scope, span || undefined);

    return callback(scope);
  });
}
/** Suppress tracing in the given callback, ensuring no spans are generated inside of it. */


function suppressTracing(callback) {
  var acs = getAcs();

  if (acs.suppressTracing) {
    return acs.suppressTracing(callback);
  }

  return withScope(scope => {
    scope.setSDKProcessingMetadata({
      [SUPPRESS_TRACING_KEY]: true
    });
    return callback();
  });
}
/**
 * Starts a new trace for the duration of the provided callback. Spans started within the
 * callback will be part of the new trace instead of a potentially previously started trace.
 *
 * Important: Only use this function if you want to override the default trace lifetime and
 * propagation mechanism of the SDK for the duration and scope of the provided callback.
 * The newly created trace will also be the root of a new distributed trace, for example if
 * you make http requests within the callback.
 * This function might be useful if the operation you want to instrument should not be part
 * of a potentially ongoing trace.
 *
 * Default behavior:
 * - Server-side: A new trace is started for each incoming request.
 * - Browser: A new trace is started for each page our route. Navigating to a new route
 *            or page will automatically create a new trace.
 */


function startNewTrace(callback) {
  return withScope(scope => {
    scope.setPropagationContext({
      traceId: generateTraceId(),
      sampleRand: Math.random()
    });
    DEBUG_BUILD && logger.info("Starting a new trace with id ".concat(scope.getPropagationContext().traceId));
    return withActiveSpan(null, callback);
  });
}

function createChildOrRootSpan(_ref) {
  var {
    parentSpan,
    spanArguments,
    forceTransaction,
    scope
  } = _ref;

  if (!hasSpansEnabled()) {
    var _span = new SentryNonRecordingSpan(); // If this is a root span, we ensure to freeze a DSC
    // So we can have at least partial data here


    if (forceTransaction || !parentSpan) {
      var dsc = _objectSpread({
        sampled: 'false',
        sample_rate: '0',
        transaction: spanArguments.name
      }, getDynamicSamplingContextFromSpan(_span));

      freezeDscOnSpan(_span, dsc);
    }

    return _span;
  }

  var isolationScope = getIsolationScope();
  var span;

  if (parentSpan && !forceTransaction) {
    span = _startChildSpan(parentSpan, scope, spanArguments);
    addChildSpanToSpan(parentSpan, span);
  } else if (parentSpan) {
    // If we forced a transaction but have a parent span, make sure to continue from the parent span, not the scope
    var _dsc = getDynamicSamplingContextFromSpan(parentSpan);

    var {
      traceId,
      spanId: parentSpanId
    } = parentSpan.spanContext();
    var parentSampled = spanIsSampled(parentSpan);
    span = _startRootSpan(_objectSpread({
      traceId,
      parentSpanId
    }, spanArguments), scope, parentSampled);
    freezeDscOnSpan(span, _dsc);
  } else {
    var {
      traceId: _traceId,
      dsc: _dsc2,
      parentSpanId: _parentSpanId,
      sampled: _parentSampled
    } = _objectSpread(_objectSpread({}, isolationScope.getPropagationContext()), scope.getPropagationContext());

    span = _startRootSpan(_objectSpread({
      traceId: _traceId,
      parentSpanId: _parentSpanId
    }, spanArguments), scope, _parentSampled);

    if (_dsc2) {
      freezeDscOnSpan(span, _dsc2);
    }
  }

  logSpanStart(span);
  setCapturedScopesOnSpan(span, scope, isolationScope);
  return span;
}
/**
 * This converts StartSpanOptions to SentrySpanArguments.
 * For the most part (for now) we accept the same options,
 * but some of them need to be transformed.
 */


function parseSentrySpanArguments(options) {
  var exp = options.experimental || {};

  var initialCtx = _objectSpread({
    isStandalone: exp.standalone
  }, options);

  if (options.startTime) {
    var ctx = _objectSpread({}, initialCtx);

    ctx.startTimestamp = spanTimeInputToSeconds(options.startTime);
    delete ctx.startTime;
    return ctx;
  }

  return initialCtx;
}

function getAcs() {
  var carrier = getMainCarrier();
  return getAsyncContextStrategy(carrier);
}

function _startRootSpan(spanArguments, scope, parentSampled) {
  var _currentPropagationCo;

  var client = getClient();
  var options = (client === null || client === void 0 ? void 0 : client.getOptions()) || {};
  var {
    name = '',
    attributes
  } = spanArguments;
  var currentPropagationContext = scope.getPropagationContext();
  var [sampled, sampleRate, localSampleRateWasApplied] = scope.getScopeData().sdkProcessingMetadata[SUPPRESS_TRACING_KEY] ? [false] : sampleSpan(options, {
    name,
    parentSampled,
    attributes,
    parentSampleRate: parseSampleRate((_currentPropagationCo = currentPropagationContext.dsc) === null || _currentPropagationCo === void 0 ? void 0 : _currentPropagationCo.sample_rate)
  }, currentPropagationContext.sampleRand);
  var rootSpan = new SentrySpan(_objectSpread(_objectSpread({}, spanArguments), {}, {
    attributes: _objectSpread({
      [SEMANTIC_ATTRIBUTE_SENTRY_SOURCE]: 'custom',
      [SEMANTIC_ATTRIBUTE_SENTRY_SAMPLE_RATE]: sampleRate !== undefined && localSampleRateWasApplied ? sampleRate : undefined
    }, spanArguments.attributes),
    sampled
  }));

  if (!sampled && client) {
    DEBUG_BUILD && logger.log('[Tracing] Discarding root span because its trace was not chosen to be sampled.');
    client.recordDroppedEvent('sample_rate', 'transaction');
  }

  if (client) {
    client.emit('spanStart', rootSpan);
  }

  return rootSpan;
}
/**
 * Creates a new `Span` while setting the current `Span.id` as `parentSpanId`.
 * This inherits the sampling decision from the parent span.
 */


function _startChildSpan(parentSpan, scope, spanArguments) {
  var {
    spanId,
    traceId
  } = parentSpan.spanContext();
  var sampled = scope.getScopeData().sdkProcessingMetadata[SUPPRESS_TRACING_KEY] ? false : spanIsSampled(parentSpan);
  var childSpan = sampled ? new SentrySpan(_objectSpread(_objectSpread({}, spanArguments), {}, {
    parentSpanId: spanId,
    traceId,
    sampled
  })) : new SentryNonRecordingSpan({
    traceId
  });
  addChildSpanToSpan(parentSpan, childSpan);
  var client = getClient();

  if (client) {
    client.emit('spanStart', childSpan); // If it has an endTimestamp, it's already ended

    if (spanArguments.endTimestamp) {
      client.emit('spanEnd', childSpan);
    }
  }

  return childSpan;
}

function getParentSpan(scope) {
  var span = _getSpanForScope(scope);

  if (!span) {
    return undefined;
  }

  var client = getClient();
  var options = client ? client.getOptions() : {};

  if (options.parentSpanIsAlwaysRootSpan) {
    return getRootSpan(span);
  }

  return span;
}

function getActiveSpanWrapper(parentSpan) {
  return parentSpan !== undefined ? callback => {
    return withActiveSpan(parentSpan, callback);
  } : callback => callback();
}

export { continueTrace, startInactiveSpan, startNewTrace, startSpan, startSpanManual, suppressTracing, withActiveSpan };